import { UserData } from '@dap/memberadmin/types';
import { getLanguageKeyFromUserLanguage, getUserLanguageSetting } from '@dap/memberadmin/utils';
import { LanguageCapitalLettersCode } from '@shared/types';
import { getDTOLanguageKeyFromResource } from '@shared/utils';
import { setDefaultOptions as setDateFnsLocale } from 'date-fns';
import { enGB, nb, sv } from 'date-fns/locale';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { brandadminApi } from '../../api/brandadminApi';
import { RoleTags } from '../../api/tags';
import { useGetUserQuery, useUpdateUserMutation } from '../../api/userApi';
import { newErrorMessage } from '../snackbar/snackbarReducer';
import { useAppDispatch } from '../useRedux';
import { useAppTFunction } from '@dap/common/i18n';
// import { mockUserData } from '@dap/sanity/utils';

export const useUser = () => {
  const { data } = useGetUserQuery();
  if (!data) {
    throw new Error('No user signed in');
  }

  return data;
  // return mockUserData(data);
};

export const useUserState = () => {
  const state = useGetUserQuery();
  return state;
};

function getPreferredLocale(languageCode: LanguageCapitalLettersCode) {
  switch (languageCode) {
    case LanguageCapitalLettersCode.Norwegian:
      return nb;
    case LanguageCapitalLettersCode.English:
      return enGB;
    case LanguageCapitalLettersCode.Swedish:
      return sv;
    default:
      return nb;
  }
}

export function useLanguageSelection() {
  const { i18n } = useTranslation();
  const { changeLanguage, language } = i18n;
  const t = useAppTFunction();
  const dispatch = useAppDispatch();
  const [updateUser] = useUpdateUserMutation();

  const switchLanguage = async (
    languageCapitalCode: LanguageCapitalLettersCode,
    previousLanguageCapitalCode?: LanguageCapitalLettersCode
  ) => {
    const preferredLanguage = languageCapitalCode;
    const isoTwoLetterLanguageCode = getLanguageKeyFromUserLanguage(languageCapitalCode);
    const languageChanged = isoTwoLetterLanguageCode !== language;

    if (languageChanged) {
      await changeLanguage(isoTwoLetterLanguageCode);
    }

    dayjs.locale(isoTwoLetterLanguageCode);
    setDateFnsLocale({ locale: getPreferredLocale(languageCapitalCode) });

    if (previousLanguageCapitalCode !== languageCapitalCode) {
      try {
        await updateUser({ preferredLanguage }).unwrap();
      } catch {
        dispatch(newErrorMessage(t('common.error.update')));
      }
    }

    // Refetch after the preference is saved. IAM can resolve role titles from
    // Accept-Language or the stored preference; either way the new language is in place.
    if (languageChanged) {
      dispatch(brandadminApi.util.invalidateTags([RoleTags.ROLES]));
    }
  };

  const initUserLanguage = (userData: UserData) => {
    const userLanguageCode = getUserLanguageSetting(userData);
    const languageCapitalLettersCode = getDTOLanguageKeyFromResource(userLanguageCode);

    if (userLanguageCode !== language) {
      switchLanguage(languageCapitalLettersCode, languageCapitalLettersCode);
    }

    setDateFnsLocale({ locale: getPreferredLocale(languageCapitalLettersCode) });
  };

  return {
    initUserLanguage,
    switchLanguage,
    language,
  };
}
